import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ApiError, prospectsApi, type ProspectRow, type ProspectsResponse } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

type StatusFilter = "all" | "queued" | "approved" | "registered" | "declined" | "failed";

const STATUS_TABS: Array<{ key: StatusFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "queued", label: "Queued" },
  { key: "approved", label: "Approved" },
  { key: "registered", label: "Registered" },
  { key: "declined", label: "Declined" },
  { key: "failed", label: "Failed" },
];

export function ProspectsPage() {
  const [filter, setFilter] = useState<StatusFilter>("all");
  const q = useQuery({
    queryKey: ["ops-prospects"],
    queryFn: () => prospectsApi.list(200),
    refetchInterval: 60_000,
  });

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Prospects</h1>
          <p className="text-ink-600 text-sm mt-1">
            Goldie's expired-domain pipeline — feed → Ahrefs scoring → operator review → auto-register.{" "}
            <Link to="/agents" className="text-accent hover:underline">Agents</Link>{" · "}
            <Link to="/ops/finance" className="text-accent hover:underline">Finance</Link>
          </p>
        </div>
      </div>

      <ParkedCallout />

      {q.isLoading && (
        <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>
      )}
      {q.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          Failed: {String((q.error as Error).message)}
        </div>
      )}
      {q.data && <Body data={q.data} filter={filter} setFilter={setFilter} />}
    </div>
  );
}

function ParkedCallout() {
  return (
    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
      <p className="font-semibold mb-1">Goldie is parked — feed source pending</p>
      <p className="text-blue-800 leading-relaxed">
        Infrastructure (DB, scoring, auto-register, Ahrefs probe) shipped 2026-05-05 but the discovery feed is parked while
        the operator pursues ICANN CZDS zone-file approval. The agent is in <code className="bg-white px-1 rounded text-xs">dry_run</code>{" "}
        + disabled by default; this page surfaces any prospects that have been imported manually or once the feed connector lands.
        See <code className="bg-white px-1 rounded text-xs">docs/goldie-design-notes.md</code> for the resume plan.
      </p>
    </div>
  );
}

function Body({
  data,
  filter,
  setFilter,
}: {
  data: ProspectsResponse;
  filter: StatusFilter;
  setFilter: (f: StatusFilter) => void;
}) {
  const filtered = useMemo(
    () => (filter === "all" ? data.prospects : data.prospects.filter((p) => p.status === filter)),
    [data.prospects, filter],
  );

  return (
    <div className="space-y-6">
      <SummaryCard data={data} />
      <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-ink-100 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold">Prospect queue</h2>
          <div className="flex flex-wrap gap-1 text-xs">
            {STATUS_TABS.map((t) => {
              const count = t.key === "all" ? data.prospects.length : (data.summary[t.key] ?? 0);
              const active = filter === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setFilter(t.key)}
                  className={
                    "px-2.5 py-1 rounded-md font-medium transition-colors " +
                    (active
                      ? "bg-ink-900 text-white"
                      : "bg-ink-50 text-ink-600 hover:bg-ink-100")
                  }
                >
                  {t.label} <span className={active ? "text-ink-300" : "text-ink-400"}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-500">
            {data.prospects.length === 0
              ? "No prospects yet. Goldie hasn't been promoted out of dry_run, or the feed connector isn't live."
              : `No prospects with status "${filter}".`}
          </div>
        ) : (
          <ProspectTable rows={filtered} />
        )}
      </div>
      <QuickReference />
    </div>
  );
}

function SummaryCard({ data }: { data: ProspectsResponse }) {
  const total = data.prospects.length;
  const get = (k: string) => data.summary[k] ?? 0;
  const spendUsd = data.spend30dCents / 100;
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">Summary</h2>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
        <Stat label="Queued" value={get("queued")} accent={get("queued") > 0} />
        <Stat label="Approved" value={get("approved")} positive={get("approved") > 0} />
        <Stat label="Registered" value={get("registered")} positive={get("registered") > 0} />
        <Stat label="Declined" value={get("declined")} />
        <Stat label="Failed" value={get("failed")} warn={get("failed") > 0} />
        <Stat
          label="30d spend"
          value={`$${spendUsd.toFixed(2)}`}
          hint={`Cap $30/day · ${total} total prospects`}
        />
      </div>
    </div>
  );
}

function ProspectTable({ rows }: { rows: ProspectRow[] }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<ProspectRow>(
    rows,
    {
      domain: (p) => p.domain,
      niche: (p) => p.nicheMatch,
      score: (p) => p.score,
      dr: (p) => p.ahrefsDr,
      spam: (p) => p.spamVerdict,
      status: (p) => p.status,
      discovered: (p) => p.discoveredAt,
    },
  );
  return (
    <table className="w-full text-sm">
      <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
        <tr>
          <SortHeader label="Domain" sortKey="domain" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
          <SortHeader label="Niche / City" sortKey="niche" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
          <SortHeader label="Score" sortKey="score" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" />
          <SortHeader label="DR / BL" sortKey="dr" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" title="Sorted by Ahrefs DR (the dominant trust signal)." />
          <SortHeader label="Spam" sortKey="spam" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
          <SortHeader label="Status" sortKey="status" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
          <SortHeader label="Discovered" sortKey="discovered" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
          <th className="text-right px-4 py-3"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-100">
        {sortedRows.map((p) => (
          <ProspectRowView key={p.id} p={p} />
        ))}
      </tbody>
    </table>
  );
}

function ProspectRowView({ p }: { p: ProspectRow }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<"approve" | "decline" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: () => prospectsApi.approve(p.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ops-prospects"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
    onSettled: () => setPending(null),
  });
  const decline = useMutation({
    mutationFn: () => prospectsApi.decline(p.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ops-prospects"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
    onSettled: () => setPending(null),
  });

  const niche = p.nicheMatch ?? "—";
  const city = [p.cityMatch, p.stateMatch].filter(Boolean).join(", ") || "—";
  const drBl =
    p.ahrefsDr != null || p.ahrefsBacklinks != null
      ? `${p.ahrefsDr ?? "?"} / ${p.ahrefsBacklinks?.toLocaleString() ?? "?"}`
      : "—";
  const actionable = p.status === "queued" || p.status === "skipped";

  return (
    <tr className="hover:bg-ink-50/50 align-top">
      <td className="px-4 py-3">
        <a
          href={`https://${p.domain}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-ink-900 hover:text-accent hover:underline"
        >
          {p.domain}
        </a>
        <div className="text-[11px] text-ink-400 mt-0.5">{p.source}</div>
        {p.registeredSiteId && (
          <div className="text-[11px] mt-0.5">
            <Link to={`/sites/${p.registeredSiteId}`} className="text-accent hover:underline">
              → site
            </Link>
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-ink-700">
        <div>{niche}</div>
        <div className="text-ink-500">{city}</div>
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {p.score != null ? <strong>{p.score.toFixed(1)}</strong> : <span className="text-ink-400">—</span>}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-xs text-ink-700">{drBl}</td>
      <td className="px-4 py-3 text-xs">
        <SpamBadge verdict={p.spamVerdict} count={p.spamCount} sample={p.spamSampleSize} />
      </td>
      <td className="px-4 py-3 text-xs">
        <StatusBadge status={p.status} />
        {p.statusReason && (
          <div className="text-[11px] text-ink-500 mt-0.5 max-w-xs">{p.statusReason}</div>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-ink-500 whitespace-nowrap">
        {new Date(p.discoveredAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
      </td>
      <td className="px-4 py-3 text-right">
        {actionable ? (
          <div className="flex justify-end gap-1.5">
            <button
              onClick={() => {
                setError(null);
                setPending("approve");
                approve.mutate();
              }}
              disabled={pending !== null}
              className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {pending === "approve" ? "…" : "Approve"}
            </button>
            <button
              onClick={() => {
                setError(null);
                setPending("decline");
                decline.mutate();
              }}
              disabled={pending !== null}
              className="text-xs px-2 py-1 rounded bg-ink-100 text-ink-700 hover:bg-ink-200 disabled:opacity-50"
            >
              {pending === "decline" ? "…" : "Decline"}
            </button>
          </div>
        ) : (
          <span className="text-[11px] text-ink-400">
            {p.resolvedAt ? new Date(p.resolvedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
          </span>
        )}
        {error && <div className="text-[11px] text-red-700 mt-1">{error}</div>}
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === "queued"
      ? "bg-purple-100 text-purple-800"
      : status === "approved"
        ? "bg-blue-100 text-blue-800"
        : status === "registered"
          ? "bg-green-100 text-green-800"
          : status === "declined"
            ? "bg-ink-100 text-ink-700"
            : status === "failed"
              ? "bg-red-100 text-red-800"
              : "bg-ink-100 text-ink-600";
  return (
    <span className={`uppercase tracking-wide text-[10px] font-semibold px-1.5 py-0.5 rounded ${color}`}>
      {status}
    </span>
  );
}

function SpamBadge({
  verdict,
  count,
  sample,
}: {
  verdict: string | null;
  count: number | null;
  sample: number | null;
}) {
  if (!verdict) return <span className="text-ink-400">—</span>;
  const color =
    verdict === "clean"
      ? "text-green-700"
      : verdict === "mild"
        ? "text-amber-700"
        : verdict === "spammy"
          ? "text-red-700"
          : "text-ink-500";
  const ratio = count != null && sample != null && sample > 0 ? `${count}/${sample}` : null;
  return (
    <span className={color}>
      <span className="font-medium">{verdict}</span>
      {ratio && <span className="text-ink-400 ml-1">({ratio})</span>}
    </span>
  );
}

function Stat({
  label,
  value,
  positive,
  warn,
  accent,
  hint,
}: {
  label: string;
  value: string | number;
  positive?: boolean;
  warn?: boolean;
  accent?: boolean;
  hint?: string;
}) {
  const color = warn
    ? "text-amber-700"
    : positive
      ? "text-green-700"
      : accent
        ? "text-purple-700"
        : "text-ink-900";
  return (
    <div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
      <div className="text-[11px] text-ink-500 uppercase tracking-wide" title={hint}>
        {label}
      </div>
      {hint && <div className="text-[10px] text-ink-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function QuickReference() {
  return (
    <div className="bg-ink-50 border border-ink-100 rounded-lg p-4 text-sm text-ink-600">
      <p className="font-semibold mb-2">Quick reference</p>
      <ul className="space-y-1 text-xs">
        <li><code className="bg-white px-1 rounded">rankforge prospect mine</code> — force one Goldie tick now</li>
        <li><code className="bg-white px-1 rounded">rankforge prospect list --status queued</code> — same data, CLI shape</li>
        <li><code className="bg-white px-1 rounded">rankforge prospect approve &lt;domain&gt;</code> / <code className="bg-white px-1 rounded">decline &lt;domain&gt;</code> — equivalent to the row buttons</li>
        <li><code className="bg-white px-1 rounded">rankforge agents promote goldie-prospector --phase confirm</code> — promote out of dry_run once trusted</li>
      </ul>
    </div>
  );
}
